// src/pages/OurProductsPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import { useLocale } from "../contexts/LocaleContext";
import { getProductPageData } from "../api/apiService";
import BrandBubblesGrid from "../components/products/BrandBubblesGrid";
import BrandSidebar from "../components/products/BrandSidebar";
import BrandHeader from "../components/products/BrandHeader";
import SubCategoryDescription from "../components/products/SubCategoryDescription";
import ProductList from "../components/products/ProductList";
import ProductThemeShell from "../components/products/ProductThemeShell";
import { resolveProductThemeFromBrand } from "../utils/productThemes";

/** Normalize brand introImg to the same shape as sub-category description_image. */
const normalizeIntroImage = (introImg) => {
  if (!introImg) return null;
  const entry = Array.isArray(introImg) ? introImg[0] : introImg;
  return entry?.url ? entry : null;
};

const buildBrandIntroSection = (brand) => {
  if (!brand) return null;
  const description_image = normalizeIntroImage(brand.introImg);
  if (!brand.Introduction && !description_image?.url) return null;
  return {
    name: brand.name,
    description: brand.Introduction,
    description_image,
  };
};

const OurProductsPage = () => {
  const { locale } = useLocale();
  const location = useLocation();
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);

  // Safely render text fields that might come from rich text / object structures
  const safeText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map((v) => safeText(v)).join(" ");
    }
    if (typeof value === "object") {
      if (Array.isArray(value.children)) {
        return value.children.map((child) => safeText(child)).join(" ");
      }
      if (typeof value.text === "string") return value.text;
    }
    return "";
  };

  useEffect(() => {
    const fetchProductsContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getProductPageData(locale);
        setProductsData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setProductsData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductsContent();
  }, [locale]);

  const brands = useMemo(() => {
    return productsData?.cas_product_categories || [];
  }, [productsData]);

  const subCategories = useMemo(() => {
    return productsData?.cas_sub_categories || [];
  }, [productsData]);

  // Keep selectedBrandId and selectedSubCategoryId in sync with URL
  // - ???? /products?selectedBrandId ??null??????????
  // - /products/xxx??????? cas_product_categories.url ???????brand
  // - /products/xxx/yyy??????? list item ??url ???????brand ??sub-category
  useEffect(() => {
    if (!brands || brands.length === 0) return;

    const pathname = location.pathname.toLowerCase();
    
    // First, try to match brand URL directly (e.g., /products/tracetek)
    const matchedBrandByUrl =
      brands.find(
        (b) =>
          typeof b.url === "string" &&
          b.url.toLowerCase() === pathname
      ) || null;

    if (matchedBrandByUrl) {
      // Just brand URL, no sub-category
      if (selectedBrandId !== matchedBrandByUrl.id) {
        setSelectedBrandId(matchedBrandByUrl.id);
      }
      // ???????????????sub-category???????brand URL???? sub-category
      if (selectedSubCategoryId !== null) {
        setSelectedSubCategoryId(null);
      }
      return;
    }

    // If not matched by brand URL, try to find matching list item URL
    // This handles cases like /products/tracetek/alarm
    let matchedBrand = null;
    let matchedSubCategory = null;
    let matchingListItem = null;

    // Search through all brands to find matching list item
    for (const brand of brands) {
      if (brand.list && Array.isArray(brand.list)) {
        const foundItem = brand.list.find(
          (item) => item.url && item.url.toLowerCase() === pathname
        );
        
        if (foundItem) {
          matchingListItem = foundItem;
          matchedBrand = brand;
          
          // Now find the corresponding sub-category
          // Match by comparing list item title with sub-category name
          const brandSubs = subCategories.filter(
            (sub) => sub?.type && brand?.name && sub.type === brand.name
          );
          
          for (const sub of brandSubs) {
            const itemTitle = safeText(foundItem.title);
            const subName = safeText(sub.name);
            if (itemTitle === subName) {
              matchedSubCategory = sub;
              break;
            }
          }
          break;
        }
      }
    }

    if (matchedBrand) {
      if (matchedSubCategory) {
        // Found both brand and sub-category
        if (selectedBrandId !== matchedBrand.id || selectedSubCategoryId !== matchedSubCategory.id) {
          setSelectedBrandId(matchedBrand.id);
          setSelectedSubCategoryId(matchedSubCategory.id);
        }
      } else {
        // Found brand but no sub-category match
        if (selectedBrandId !== matchedBrand.id) {
          setSelectedBrandId(matchedBrand.id);
          setSelectedSubCategoryId(null);
        }
      }
    } else {
      // No match at all, reset to initial state
      if (selectedBrandId !== null) {
        setSelectedBrandId(null);
        setSelectedSubCategoryId(null);
      }
    }
  }, [brands, subCategories, location.pathname, selectedBrandId, selectedSubCategoryId]);

  const selectedBrand = useMemo(() => {
    if (!brands || brands.length === 0) return null;
    if (!selectedBrandId) return null;
    return brands.find((brand) => brand.id === selectedBrandId) || null;
  }, [selectedBrandId, brands]);

  // Sub-categories that belong to the selected brand (by matching type to brand.name)
  const brandSubCategories = useMemo(() => {
    if (!selectedBrand) return [];
    return subCategories.filter(
      (sub) => sub?.type && selectedBrand?.name && sub.type === selectedBrand.name
    );
  }, [subCategories, selectedBrand]);

  // Keep selectedSubCategoryId in sync with current brand's sub-categories
  // - If current brand?????? subCategories???? selectedSubCategoryId
  // - ??? selectedSubCategoryId ???????brand???????????????????1 ????
  useEffect(() => {
    if (!brandSubCategories || brandSubCategories.length === 0) {
      if (selectedSubCategoryId !== null) {
        setSelectedSubCategoryId(null);
      }
      return;
    }

    const existsInCurrentBrand = brandSubCategories.some(
      (sub) => sub.id === selectedSubCategoryId
    );

    if (!existsInCurrentBrand && selectedSubCategoryId !== null) {
      setSelectedSubCategoryId(null);
    }
  }, [brandSubCategories, selectedSubCategoryId]);

  const activeSubCategory = useMemo(() => {
    if (!brandSubCategories || brandSubCategories.length === 0) return null;
    if (!selectedSubCategoryId) return null;

    const found = brandSubCategories.find(
      (sub) => sub.id === selectedSubCategoryId
    );
    return found || null;
  }, [brandSubCategories, selectedSubCategoryId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  if (!productsData) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
      </div>
    );
  }

  const { banner, title: sidebarTitle } = productsData;
  const bannerTitle = banner?.title || "Our Products";
  const bannerImageUrl = banner?.image?.[0]?.url;
  const bannerTitleIsWhite = banner?.titleColorIsWhite ?? true;

  const productThemeId = selectedBrand
    ? resolveProductThemeFromBrand(selectedBrand)
    : undefined;

  const introSection = activeSubCategory
    ? activeSubCategory
    : buildBrandIntroSection(selectedBrand);

  const showProductsSectionTitle =
    introSection &&
    (introSection.description || introSection.description_image?.url);

  return (
    <ProductThemeShell themeId={productThemeId}>
    <div className="our-products-page min-h-screen">
      {/* Banner */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={bannerTitleIsWhite}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-72 lg:h-80"
      />

      {/* Main Content */}
      <section className="product-section-surface w-full py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* If no brand selected yet: show brand bubbles grid
        {!selectedBrand && (
          <BrandBubblesGrid
            brands={brands}
            sidebarTitle={sidebarTitle}
            safeText={safeText}
          />
        )} */}

        {/* When a brand is selected: show full layout with left sidebar + right content */}
        {selectedBrand && (
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left: Brand navigation */}
            <BrandSidebar
              brands={brands}
              subCategories={subCategories}
              selectedBrandId={selectedBrandId}
              selectedSubCategoryId={selectedSubCategoryId}
              sidebarTitle={sidebarTitle}
              setSelectedBrandId={setSelectedBrandId}
              setSelectedSubCategoryId={setSelectedSubCategoryId}
              safeText={safeText}
            />

            {/* Right: Selected Brand products */}
            <div className="flex-1 min-w-0">
              <div className="product-panel-elevated rounded-3xl p-6 sm:p-8 lg:p-10">
                {/* Brand header */}
                <BrandHeader selectedBrand={selectedBrand} safeText={safeText} />

                {/* Brand or sub-category intro (description + image) */}
                <SubCategoryDescription
                  activeSubCategory={introSection}
                  safeText={safeText}
                />

                {/* Product list */}
                <ProductList
                  items={
                    (activeSubCategory &&
                      activeSubCategory.list &&
                      activeSubCategory.list.length > 0 &&
                      activeSubCategory.list) ||
                    selectedBrand.list ||
                    []
                  }
                  safeText={safeText}
                  sectionTitle={
                    showProductsSectionTitle ? "Products" : undefined
                  }
                />
              </div>
            </div>
          </div>
        )}
        </div>
      </section>
    </div>
    </ProductThemeShell>
  );
};

export default OurProductsPage;
